import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  TextField,
  Tooltip
} from '@mui/material';
import { getLabel } from '../../../utils/helper';
import { setOpenAsset } from './OpenAssetSlice';
import useAssetListHook from '../hooks/useAssetListHook';
import StarIcon from '@mui/icons-material/Star';
import InfoIcon from '@mui/icons-material/Info';

// Constants for event names - duplicated in each file that needs them
const DEFAULT_ASSET_CHANGED = 'defaultAssetChanged';

const AssetSwitcher = (props) => {
  const scope = useSelector((state) => state.container.scope);
  const [selectedAsset, setselectedAsset] = useState({});
  const [assetsDropdown, setAssetDropdown] = useState([]);
  const openAsset = useSelector((state) => state.openAsset);
  const dispatch = useDispatch();
  const {assetList, fetchList} = useAssetListHook();
  const [isDefaultAsset, setIsDefaultAsset] = useState(false);

  useEffect(() => {
    if(assetList.isFetchSuccess && assetList.assets.length > 0) {
      try {
        // Get default asset from localStorage
        const storedDefault = localStorage.getItem('defaultAsset');
        let defaultAssetIdent = null;
        if (storedDefault) {
          const defaultAsset = JSON.parse(storedDefault);
          if (defaultAsset.projectIdent === scope.projectIdent) {
            defaultAssetIdent = defaultAsset.ident;
          }
        }

        const list = assetList.assets.map((a) => ({
          index: a.index,
          name: a.name,
          ident: a.assetIdent,
          isDefault: a.assetIdent === defaultAssetIdent,
          subAssetList: a?.subAssetListDto?.map((s) => ({
            value: s.name,
            id: s.subAssetIdent,
          })),
          users: a?.userDtos?.map((u) => ({
            label: `${u.firstName} ${u.lastName}`,
            value: u.emailAddress,
          })),
          projectIdent: scope.projectIdent,
        }));
        
        setAssetDropdown(list);

        // Auto-selection logic for assets
        let shouldAutoSelect = false;
        let assetToSelect = null;

        // First check if there's a default asset for this project
        if (defaultAssetIdent) {
          assetToSelect = list.find(asset => asset.ident === defaultAssetIdent);
          if (assetToSelect) {
            shouldAutoSelect = true;
          }
        }

        // If no default asset found and there's only one asset available, auto-select it
        if (!shouldAutoSelect && list.length === 1) {
          assetToSelect = list[0];
          shouldAutoSelect = true;
          
          // Since there's only one option, also set it as default automatically
          setAssetAsDefault(assetToSelect);
        }

        // Perform auto-selection if applicable
        if (shouldAutoSelect && assetToSelect) {
          getSelectedAsset(assetToSelect);
        }
      } catch (error) {
        console.error('Error loading assets:', error);
      }
    } else if(!assetList.isFetchSuccess) {
      fetchList();
    }
  }, [assetList, scope.projectIdent, fetchList]);

  // Listen for default asset changes
  useEffect(() => {
    const handleDefaultAssetChange = (event) => {
      if (event.detail?.ident) {
        const assetIdent = event.detail.ident;
        setAssetDropdown(prev => prev.map(asset => ({
          ...asset,
          isDefault: asset.ident === assetIdent
        })));
        
        if (selectedAsset?.ident === assetIdent) {
          setIsDefaultAsset(true);
        }
      } else if (event.detail?.removed) {
        setAssetDropdown(prev => prev.map(asset => ({
          ...asset,
          isDefault: false
        })));
        setIsDefaultAsset(false);
      }
    };

    window.addEventListener(DEFAULT_ASSET_CHANGED, handleDefaultAssetChange);
    return () => {
      window.removeEventListener(DEFAULT_ASSET_CHANGED, handleDefaultAssetChange);
    };
  }, [selectedAsset]);

  // Update selected asset when openAsset changes
  useEffect(() => {
    if(assetsDropdown.length > 0 && openAsset?.ident) {
      const found = assetsDropdown.find((f) => f.ident === openAsset.ident);
      if (found) {
        setselectedAsset(found);
        setIsDefaultAsset(found.isDefault);
      }
    }
  }, [assetsDropdown, openAsset]);

  const getSelectedAsset = (val) => {
    if (props.dirtyState) {
      props.setBlockState(val);
    } else {
      setselectedAsset(val);
      setIsDefaultAsset(val.isDefault);
      dispatch(setOpenAsset({
        ...val,
        subAssetIdent: null, // Clear sub-asset when switching assets
        subAssetName: null
      }));
    }
  };

  const setAssetAsDefault = (asset) => {
    try {
      // Set default asset in localStorage
      localStorage.setItem('defaultAsset', JSON.stringify({
        ident: asset.ident,
        name: asset.name,
        projectIdent: scope.projectIdent,
        timestamp: new Date().getTime()
      }));

      // Update UI
      setAssetDropdown(prev => prev.map(item => ({
        ...item,
        isDefault: item.ident === asset.ident
      })));
      
      // Update selected asset if it matches
      if (selectedAsset?.ident === asset.ident) {
        setIsDefaultAsset(true);
      }
      
      // Notify about asset default change
      window.dispatchEvent(new CustomEvent(DEFAULT_ASSET_CHANGED, {
        detail: {
          ident: asset.ident,
          name: asset.name,
          projectIdent: scope.projectIdent
        }
      }));
    } catch (error) {
      console.error('Error setting default asset:', error);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Autocomplete
        id="assetsOptions"
        size="small"
        autoHighlight={true}
        style={{ padding: 5, flexGrow: 1 }}
        value={selectedAsset || ''}
        options={assetsDropdown}
        onChange={(event, newValue) => {
          if (newValue?.ident) {
            getSelectedAsset(newValue);
          }
        }}
        getOptionLabel={(option) => option.name || ''}
        renderOption={(props, option) => (
          <li {...props}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <span style={{ flexGrow: 1 }}>{option.name}</span>
              {option.isDefault && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Tooltip title="Default Asset">
                    <StarIcon fontSize="small" style={{ color: '#FFD700' }} />
                  </Tooltip>
                  <Tooltip title="Set default asset on defect page">
                    <InfoIcon fontSize="small" style={{ color: '#1976d2', cursor: 'pointer' }} />
                  </Tooltip>
                </div>
              )}
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            minRows={4}
            variant="outlined"
            size="small"
            label={getLabel('_select asset')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {selectedAsset?.ident && isDefaultAsset && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Tooltip title="Default Asset">
                        <StarIcon fontSize="small" style={{ color: '#FFD700' }} />
                      </Tooltip>
                      <Tooltip title="Set default asset on defect page">
                        <InfoIcon fontSize="small" style={{ color: '#1976d2', cursor: 'pointer' }} />
                      </Tooltip>
                    </div>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
};

AssetSwitcher.propTypes = {
  dirtyState: PropTypes.bool,
  setBlockState: PropTypes.func,
  filterAssetList: PropTypes.array,
};

export default AssetSwitcher;